/**
 * Region Context
 *
 * Owns the Auth0 tenant claims — the cross-region index. `GET /users/me/tenants`
 * is region-local (each regional API only sees its own database), so the claims
 * are what tell the SPA which regions to ask and which `/api/:region` base URL
 * to use. They are never an authority on membership or role: the API resolves
 * both from its database on every request.
 *
 * Provides:
 * - Parsed tenant claims from JWT
 * - Token refresh capability
 *
 * The API base URL is NOT held here as state. It is derived from the region of
 * the tenant currently in context (see api-context), so a switch to a tenant in
 * another region takes effect on the same render as the switch itself
 * (ESCLOUD-269).
 */

import { useAuth0 } from '@auth0/auth0-react';
import type { IParsedTenantClaim } from '@escloud/types';
import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

import { parseEmailVerifiedClaim, parseTenantClaimsFromToken } from '@/lib/jwt';
import { errorIds, logError } from '@/lib/log-error';

interface RegionContextType {
    /** Tenant claims parsed from Auth0 token */
    tenantClaims: IParsedTenantClaim[];

    /**
     * The raw verified-email claim from the same token: `true`, `false`, or
     * `undefined` when the claim is not on the token at all.
     *
     * Read it through `useEmailVerificationState()` rather than here — it is
     * the one place that turns this, `claimsLoaded` and `claimsFailed` into a
     * single verdict, so the guards, the redirect and the wizard cannot reach
     * different conclusions from the same token (ESCLOUD-286).
     *
     * Stale until refreshToken() is called — the token minted at sign-in cannot
     * know about a verification that happened after it, which is why the
     * onboarding verify-email screen polls refreshToken(). UX only: the API
     * reads the claim itself on every request.
     */
    emailVerifiedClaim: boolean | undefined;

    /** Whether claims have been loaded (true even if empty) */
    claimsLoaded: boolean;

    /**
     * The claims could not be obtained at all — a revoked or expired session, a
     * blocked token endpoint. An empty claim list is then not an answer, and
     * nothing downstream may read it as "this user has no organizations".
     */
    claimsFailed: boolean;

    /** Force refresh the Auth0 token to get updated claims */
    refreshToken: () => Promise<void>;

    /** Loading state while parsing token claims */
    isLoading: boolean;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

interface RegionProviderProps {
    children: ReactNode;
}

export function RegionProvider({ children }: RegionProviderProps) {
    const { getAccessTokenSilently, isAuthenticated, isLoading: isAuthLoading } = useAuth0();

    const [tenantClaims, setTenantClaims] = useState<IParsedTenantClaim[]>([]);
    const [emailVerifiedClaim, setEmailVerifiedClaim] = useState<boolean | undefined>(undefined);
    const [claimsLoaded, setClaimsLoaded] = useState(false);
    const [claimsFailed, setClaimsFailed] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Parse token claims when authentication state changes
    useEffect(() => {
        async function loadClaims() {
            if (isAuthLoading) {
                return;
            }

            if (!isAuthenticated) {
                setTenantClaims([]);
                setEmailVerifiedClaim(undefined);
                setClaimsFailed(false);
                setClaimsLoaded(true);
                setIsLoading(false);
                return;
            }

            try {
                const token = await getAccessTokenSilently();
                setTenantClaims(parseTenantClaimsFromToken(token));
                setEmailVerifiedClaim(parseEmailVerifiedClaim(token));
                setClaimsFailed(false);
                setClaimsLoaded(true);
            } catch (error) {
                logError(errorIds.TENANT_CLAIMS_LOAD_FAILED, 'Failed to load tenant claims', { error });
                setTenantClaims([]);
                setClaimsFailed(true);
                setClaimsLoaded(true);
            } finally {
                setIsLoading(false);
            }
        }

        loadClaims();
    }, [isAuthenticated, isAuthLoading, getAccessTokenSilently]);

    // Force token refresh to get updated claims
    const refreshToken = useCallback(async () => {
        try {
            setIsLoading(true);
            setClaimsLoaded(false);
            // Force token refresh by ignoring cache
            const token = await getAccessTokenSilently({ cacheMode: 'off' });
            setTenantClaims(parseTenantClaimsFromToken(token));
            setEmailVerifiedClaim(parseEmailVerifiedClaim(token));
            setClaimsFailed(false);
            setClaimsLoaded(true);
        } catch (error) {
            logError(errorIds.TENANT_CLAIMS_REFRESH_FAILED, 'Failed to refresh tenant claims', { error });
            setClaimsFailed(true);
            setClaimsLoaded(true);
            throw error;
        } finally {
            setIsLoading(false);
        }
    }, [getAccessTokenSilently]);

    const value: RegionContextType = {
        tenantClaims,
        emailVerifiedClaim,
        claimsLoaded,
        claimsFailed,
        refreshToken,
        isLoading,
    };

    return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
}

/**
 * Hook to access region context
 *
 * @throws Error if used outside of RegionProvider
 */
export function useRegion(): RegionContextType {
    const context = useContext(RegionContext);
    if (!context) {
        throw new Error('useRegion must be used within a RegionProvider');
    }
    return context;
}
